import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import type {
    IPromptsApiFilterParams,
    IPromptsFilterState,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/prompts-widget/types";

interface IUsePromptsUrlParamsReturn {
    apiFilterParams: IPromptsApiFilterParams;
    filterState: IPromptsFilterState;
    searchTerm: string | undefined;
}

export const usePromptsUrlParams = (): IUsePromptsUrlParamsReturn => {
    const { params } = useCurrentStateAndParams();

    const apiFilterParams = useMemo<IPromptsApiFilterParams>(
        () => ({
            ...(params.searchTerm && { searchTerm: params.searchTerm }),
            ...(params.topicsFilter && { topics: params.topicsFilter }),
            ...(params.mentionStatuses && {
                sentimentStatuses: params.mentionStatuses.split(","),
            }),
            ...(params.minVisibility && { minVisibility: Number(params.minVisibility) }),
            ...(params.maxVisibility && { maxVisibility: Number(params.maxVisibility) }),
            ...(params.brands && { brands: params.brands.split(",") }),
            ...(params.domains && { domains: params.domains.split(",") }),
            ...(params.topSourceCategories && { topSourceCategories: params.topSourceCategories.split(",") }),
            ...(params.citationGaps && { citationGap: params.citationGaps.split(",") }),
            ...(params.sentimentTone && { sentimentTone: params.sentimentTone.split(",") }),
            ...(params.citationVolatility && { citationVolatility: params.citationVolatility.split(",") }),
            ...(params.latestCitationStatuses && { latestCitationStatus: params.latestCitationStatuses.split(",") }),
            ...(params.change && { change: params.change.split(",") }),
            ...(params.sentimentShift && { sentimentShift: params.sentimentShift.split(",") }),
        }),
        [
            params.searchTerm,
            params.topicsFilter,
            params.mentionStatuses,
            params.sentimentTone,
            params.minVisibility,
            params.maxVisibility,
            params.brands,
            params.domains,
            params.topSourceCategories,
            params.citationGaps,
            params.citationVolatility,
            params.latestCitationStatuses,
            params.change,
            params.sentimentShift,
        ],
    );

    const filterState: IPromptsFilterState = useMemo(
        () => ({
            ...(params.topicsFilter && { topicsFilter: params.topicsFilter.split(",") }),
            ...(params.mentionStatuses && { mentionSentimentStatus: params.mentionStatuses.split(",") }),
            ...(params.sentimentTone && { sentimentTone: params.sentimentTone.split(",") }),
            ...((params.minVisibility || params.maxVisibility) && {
                visibility: {
                    from: params.minVisibility ? Number(params.minVisibility) : undefined,
                    to: params.maxVisibility ? Number(params.maxVisibility) : undefined,
                },
            }),
            ...(params.brands && { brands: params.brands.split(",") }),
            ...(params.domains && { domains: params.domains.split(",") }),
            ...(params.topSourceCategories && { topSourceCategories: params.topSourceCategories.split(",") }),
            ...(params.citationGaps && { citationGap: params.citationGaps.split(",") }),
            ...(params.citationVolatility && { citationVolatility: params.citationVolatility.split(",") }),
            ...(params.latestCitationStatuses && { latestCitationStatus: params.latestCitationStatuses.split(",") }),
            ...(params.change && { change: params.change.split(",") }),
            ...(params.sentimentShift && { sentimentShift: params.sentimentShift.split(",") }),
        }),
        [
            params.topicsFilter,
            params.mentionStatuses,
            params.sentimentTone,
            params.minVisibility,
            params.maxVisibility,
            params.brands,
            params.domains,
            params.topSourceCategories,
            params.citationGaps,
            params.citationVolatility,
            params.latestCitationStatuses,
            params.change,
            params.sentimentShift,
        ],
    );

    return { apiFilterParams, filterState, searchTerm: params.searchTerm };
};
